import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Input } from "@/components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, Globe, AlertCircle, Search, X, Globe2, GitBranch, FileText, Pencil, Check, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDomainValidation } from "@/hooks/useDomainValidation";
import { useDomains } from "@/hooks/useDomains";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import { Domain } from "@/types/domain";

export function DomainProtection() {
  useLanguage();
  const { user } = useAuth();

  // Hook para buscar e gerenciar domínios
  const {
    domains,
    isLoading,
    isCreating,
    isUpdating,
    isDeleting,
    createDomain,
    updateDomain,
    deleteDomain,
  } = useDomains({ userId: user?.id });

  // Estados locais para UI
  const [searchQuery, setSearchQuery] = useState("");
  const [currentInput, setCurrentInput] = useState("");
  const [currentInputError, setCurrentInputError] = useState<string | undefined>();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<Domain | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const [hasStartedDomainFlow, setHasStartedDomainFlow] = useState(false);

  const { normalizeDomain, validate: validateNewDomain } = useDomainValidation(
    domains.map(d => d.baseDomain).filter(Boolean)
  );
  const { validate: validateEditedDomain } = useDomainValidation(
    domains.filter(d => d.id !== editingId).map(d => d.baseDomain).filter(Boolean)
  );

  const handleStartEdit = (id: string, value: string) => {
    setEditingId(id);
    setEditingValue(value);
  };

  const handleSaveEdit = async (id: string) => {
    const normalized = normalizeDomain(editingValue);
    const error = validateEditedDomain(normalized);

    if (error) {
      toast.error(error);
      return;
    }

    try {
      await updateDomain(id, normalized);
      setEditingId(null);
      setEditingValue("");
    } catch (error) {
      // Erro já é tratado pelo hook com toast
      console.error('Erro ao atualizar domínio:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingValue("");
  };

  const handleAddDomain = async () => {
    if (!currentInput.trim()) {
      setCurrentInputError(translateInterfaceText("Digite um domínio válido"));
      return;
    }

    const normalized = normalizeDomain(currentInput);
    const error = validateNewDomain(normalized);

    if (error) {
      if (error.includes("já cadastrado")) {
        toast.error(translateInterfaceText("⚠️ Este domínio já está cadastrado"));
      } else {
        toast.error(translateInterfaceText("✖ O domínio inserido não é válido"));
      }
      setCurrentInputError(error);
      return;
    }

    try {
      await createDomain(normalized);
      setCurrentInput("");
      setCurrentInputError(undefined);
    } catch (error) {
      // Erro já é tratado pelo hook com toast
      console.error('Erro ao criar domínio:', error);
    }
  };

  const handleRemove = (id: string) => {
    const domain = domains.find(d => d.id === id);
    if (domain) {
      setDomainToDelete(domain);
      setDeleteDialogOpen(true);
    }
  };

  const confirmDelete = async () => {
    if (domainToDelete) {
      try {
        await deleteDomain(domainToDelete.id);
        setDeleteDialogOpen(false);
        setDomainToDelete(null);
      } catch (error) {
        // Erro já é tratado pelo hook com toast
        console.error('Erro ao deletar domínio:', error);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setDomainToDelete(null);
  };

  const handleInputChange = (value: string) => {
    setCurrentInput(value);
    setCurrentInputError(undefined);
  };

  const handleInputBlur = () => {
    if (!currentInput.trim()) {
      setCurrentInputError(undefined);
      return;
    }

    const normalized = normalizeDomain(currentInput);
    const error = validateNewDomain(normalized);

    setCurrentInputError(error || undefined);
  };

  // Ordenar domínios alfabeticamente
  const sortedDomains = [...domains].sort((a, b) => a.baseDomain.localeCompare(b.baseDomain));

  // Filtrar domínios baseado na busca
  const filteredDomains = sortedDomains.filter((domain) =>
    domain.baseDomain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading state combina query loading e mutations loading
  const isMutating = isCreating || isUpdating || isDeleting;
  return (
    <div className="max-w-[960px] w-full space-y-6">

      {/* CARD 1 - Configuração / Como Funciona */}
      <div className="bg-white rounded-xl border border-[#E9ECF3] p-5 sm:p-6">

        {/* Como funciona - Micro-cards horizontais */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">

          {/* Card 1 - Domínio exato */}
          <div className="bg-[#F8FAFC] rounded-xl border border-[#E9ECF3] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <Globe2 className="w-5 h-5 text-[#6B7280]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-semibold text-[#111]">
                  {translateInterfaceText("Domínio exato")}</h4>
                <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
                  {translateInterfaceText("exemplo.com")}</p>
              </div>
            </div>
          </div>

          {/* Card 2 - Subdomínio específico */}
          <div className="bg-[#F8FAFC] rounded-xl border border-[#E9ECF3] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <GitBranch className="w-5 h-5 text-[#6B7280]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-semibold text-[#111]">
                  {translateInterfaceText("Subdomínio específico")}</h4>
                <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
                  {translateInterfaceText("blog.exemplo.com")}</p>
              </div>
            </div>
          </div>

          {/* Card 3 - Página específica */}
          <div className="bg-[#F8FAFC] rounded-xl border border-[#E9ECF3] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-[#6B7280]" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-[13px] font-semibold text-[#111]">
                  {translateInterfaceText("Página específica")}</h4>
                <p className="text-[11px] text-[#6B7280] mt-0.5 leading-relaxed">
                  {translateInterfaceText("exemplo.com/vendas")}</p>
              </div>
            </div>
          </div>

        </div>

      </div>

      {/* CARD 2 - Domínios Cadastrados em Grid */}
      <div className="bg-white rounded-xl border border-[#E9ECF3] p-5 sm:p-6 space-y-4">
        {/* Header - mesmo estilo do "Domínios autorizados" */}
        <div className="flex items-center justify-between pb-4 border-b border-[#ECECEC]">
          <div>
            <h2 className="text-lg font-semibold text-[#111] flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-[#437DFF]" />
              {translateInterfaceText("Domínios cadastrados")}</h2>
            <p className="text-[13px] text-[#6B7280] mt-1">
              {translateInterfaceText("Lista de domínios autorizados a exibir seus vídeos")}</p>
          </div>
          <span className="text-[12px] text-[#6B7280]">
            {domains.length} {" "}{translateInterfaceText("domínio")}{domains.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Loading skeleton */}
        {isLoading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-[68px] rounded-[10px] bg-gray-100 animate-pulse" />
            ))}
          </div>
        )}

        {/* Lista de domínios - Grid 3 colunas */}
        {!isLoading && (
          <TooltipProvider delayDuration={300}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {/* Rows de domínios existentes */}
              {filteredDomains.map((domain) => (
            editingId === domain.id ? (
              /* MODO EDIÇÃO - Full width */
                <div
                  key={domain.id}
                  className="flex items-center min-h-[56px] px-4 py-3 bg-white border-2 border-[#437DFF] rounded-[10px] transition-all"
              >
                {/* Ícone Globe */}
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <Globe className="w-4 h-4 text-[#437DFF]" />
                </div>

                {/* Input de edição */}
                <input
                  autoFocus
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveEdit(domain.id);
                    if (e.key === 'Escape') handleCancelEdit();
                  }}
                  className="flex-1 ml-3 text-[14px] font-semibold bg-transparent outline-none min-w-0 text-[#111]"
                  placeholder={translateInterfaceText("dominio.com")}
                />

                {/* Ações de edição */}
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleSaveEdit(domain.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-[#437DFF] hover:bg-blue-50 transition-colors"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-[#D13636] hover:bg-red-50 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ) : (
              /* MODO VISUALIZAÇÃO - Card compacto premium sem tooltip */
              <div
                key={domain.id}
                className="flex items-center min-h-[68px] px-4 py-3 bg-white border border-[#E6E8EF] hover:border-[#437DFF]/40 hover:shadow-sm rounded-[10px] transition-all cursor-default"
              >
                {/* Coluna 1: Ícone + Info do Domínio (2 linhas) */}
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Globe className="w-4 h-4 text-[#437DFF]" />
                  </div>

                  {/* Texto: URL + Data de criação */}
                  <div className="flex flex-col min-w-0">
                    <span className="text-[14px] font-semibold truncate !text-[#000000BF]">
                      {domain.baseDomain}
                    </span>
                    <span className="text-[12px] text-[#9CA3AF] mt-1">
                      {translateInterfaceText("Criado em")}{" "}{format(new Date(domain.createdAt), "dd/MM/yyyy", { locale: getInterfaceDateLocale() })}
                    </span>
                  </div>
                </div>

                {/* Ações: Editar + Excluir */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleStartEdit(domain.id, domain.baseDomain)}
                    disabled={isMutating}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-[#6B7280] hover:text-[#437DFF] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={translateInterfaceText("Editar domínio")}
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleRemove(domain.id)}
                    disabled={isMutating}
                    className="w-8 h-8 flex items-center justify-center rounded-md text-[#6B7280] hover:text-[#D13636] hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    title={translateInterfaceText("Remover domínio")}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )
          ))}

          {/* Input para adicionar novo domínio - aparece após fluxo iniciado ou quando há domínios */}
          {(hasStartedDomainFlow || domains.length > 0) && (
            <div
              className={cn(
                "flex items-center min-h-[56px] px-4 py-3 border-2 border-dashed rounded-[10px] transition-all cursor-text group animate-fade-in",
                currentInput
                  ? "bg-white border-[#437DFF] border-solid"
                  : "bg-[#F9FAFB] border-[#D0D7E3] hover:border-[#437DFF] hover:bg-blue-50/30"
              )}
              onClick={(e) => {
                const input = e.currentTarget.querySelector('input');
                if (input) input.focus();
              }}
            >
              {/* Ícone + */}
              <div className={cn(
                "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors",
                currentInput ? "bg-[#437DFF]/10" : "bg-blue-50 group-hover:bg-[#437DFF]/10"
              )}>
                <Plus className="w-4 h-4 text-[#437DFF]" />
              </div>

              {/* Input */}
              <input
                id="domain-input"
                value={currentInput}
                onChange={(e) => handleInputChange(e.target.value)}
                onBlur={handleInputBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddDomain();
                  }
                }}
                disabled={isMutating}
                placeholder={translateInterfaceText("ex: dominio.com ou dominio.com/pagina")}
                className={cn(
                  "flex-1 ml-3 text-[14px] font-medium bg-transparent outline-none min-w-0",
                  currentInput ? "text-[#111] placeholder:text-[#9CA3AF]" : "text-[#437DFF] placeholder:text-[#437DFF]",
                  currentInputError && "text-red-500",
                  isMutating && "opacity-50 cursor-not-allowed"
                )}
              />

              {/* Botão adicionar - aparece quando tem texto */}
              {currentInput.trim() && (
                <button
                  onClick={handleAddDomain}
                  disabled={isMutating || !!currentInputError}
                  className={cn(
                    "w-8 h-8 flex items-center justify-center rounded-md transition-colors ml-2",
                    !currentInputError && !isMutating
                      ? "text-[#437DFF] hover:bg-blue-50"
                      : "text-[#D0D7E3] cursor-not-allowed"
                  )}
                >
                  {isCreating ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4" />
                  )}
                </button>
              )}
            </div>
          )}
        </div>
          </TooltipProvider>
        )}

        {/* Feedback de erro inline */}
        {currentInputError && (
          <div className="flex items-center gap-1.5 text-red-500">
            <AlertCircle className="h-3.5 w-3.5" />
            <span className="text-[11px]">{currentInputError}</span>
          </div>
        )}

        {/* Busca - aparece quando há mais de 5 domínios */}
        {domains.length > 5 && (
          <div className="flex items-center justify-between pt-4 border-t border-[#ECECEC]">
            <div className="relative w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9CA3AF]" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={translateInterfaceText("Buscar domínio...")}
                className="pl-9 pr-8 h-9 text-[12px] border-[#E6E8EF] rounded-lg"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111] transition-colors"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {searchQuery && (
              <span className="text-[11px] text-[#6B7280]">
                {filteredDomains.length} {" "}{translateInterfaceText("de")}{" "}{domains.length} {" "}{translateInterfaceText("encontrado")}{filteredDomains.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Estado vazio quando busca não encontra */}
        {searchQuery && filteredDomains.length === 0 && (
          <div className="text-center py-6 text-[#6B7280]">
            <Search className="h-6 w-6 mx-auto mb-2 opacity-50" />
            <p className="text-[12px]">{translateInterfaceText("Nenhum domínio encontrado para \"")}{searchQuery}"</p>
          </div>
        )}

        {/* Estado vazio inicial - com CTA guiado */}
        {!isLoading && domains.length === 0 && !hasStartedDomainFlow && (
          <div className="text-center py-8 text-[#6B7280]">
            <Globe className="h-8 w-8 mx-auto mb-3 opacity-40" />
            <p className="text-[13px] font-medium">{translateInterfaceText("Nenhum domínio cadastrado")}</p>
            <p className="text-[11px] mt-1 mb-4">{translateInterfaceText("Adicione domínios para proteger sua VSL")}</p>

            {/* Botão CTA primário */}
            <button
              onClick={() => {
                setHasStartedDomainFlow(true);
                // Focus no input após render
                setTimeout(() => {
                  const input = document.getElementById('domain-input');
                  if (input) input.focus();
                }, 100);
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#437DFF] hover:bg-[#3166E8] text-white text-[13px] font-medium rounded-[10px] transition-all shadow-sm"
            >
              <Plus className="w-4 h-4" />
              {translateInterfaceText("Adicionar primeiro domínio")}</button>
          </div>
        )}

        {/* Estado vazio após iniciar fluxo - mostra apenas texto */}
        {!isLoading && domains.length === 0 && hasStartedDomainFlow && !currentInput && (
          <div className="text-center py-6 text-[#6B7280]">
            <Globe className="h-8 w-8 mx-auto mb-2 opacity-40" />
            <p className="text-[13px]">{translateInterfaceText("Nenhum domínio cadastrado")}</p>
            <p className="text-[11px] mt-1">{translateInterfaceText("Adicione domínios para proteger sua VSL")}</p>
          </div>
        )}
      </div>

      {/* Modal de Confirmação de Exclusão - Premium 420px */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent
          className={cn(
            "w-[calc(100vw-2rem)] md:w-[45vh]",
            "mx-auto",
            "rounded-xl border-0 bg-white",
            "p-6",
            "shadow-[0_14px_40px_rgba(0,0,0,0.18)]",
            "animate-[scaleIn_0.18s_ease-out]"
          )}
        >
          <AlertDialogHeader className="space-y-2">
            <AlertDialogTitle className="text-xl font-semibold text-[#111]">
              {translateInterfaceText("Remover domínio?")}</AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#4B5563] leading-relaxed">
              {translateInterfaceText("O domínio \"")}<span className="font-semibold text-[#111]">{domainToDelete?.baseDomain}</span>{translateInterfaceText("\" será removido da lista.")}<br />
              <span className="text-[#6B7280]">{translateInterfaceText("Esta ação não bloqueia vídeos já incorporados anteriormente.")}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-end gap-3 pt-6">
            <AlertDialogCancel
              onClick={cancelDelete}
              disabled={isDeleting}
              className="h-11 px-5 rounded-[10px] border-border text-muted-foreground hover:bg-muted hover:text-[#64748b] transition-colors"
            >
              {translateInterfaceText("Voltar")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={isDeleting}
              className="h-10 px-5 bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-[10px] text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isDeleting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {translateInterfaceText("Removendo...")}</div>
              ) : (
                translateInterfaceText("Remover")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
